import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

type ErrorStateTone = 'brand' | 'danger';

type ErrorStateProps = {
  tone?: ErrorStateTone;
  icon?: ReactNode;
  title: string;
  description?: string;
  retryLabel: string;
  onRetry: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  className?: string;
};

const TONE_CLASSES: Record<ErrorStateTone, { border: string; circle: string }> = {
  brand: { border: 'border-brand/20', circle: 'bg-brand-soft text-brand' },
  danger: { border: 'border-rose-200/70', circle: 'bg-rose-50 text-rose-500' },
};

function DefaultIcon({ tone }: { tone: ErrorStateTone }) {
  if (tone === 'danger') {
    return (
      <span className="text-3xl" aria-hidden="true">
        ⚠️
      </span>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M10.3 4.2 3.4 16.1A2 2 0 0 0 5.1 19.1h13.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" strokeLinejoin="round" />
      <path d="M12 9v4" strokeLinecap="round" />
      <path d="M12 16.2h.01" strokeLinecap="round" />
    </svg>
  );
}

export default function ErrorState({
  tone = 'brand',
  icon,
  title,
  description,
  retryLabel,
  onRetry,
  secondaryLabel,
  onSecondary,
  className,
}: ErrorStateProps) {
  const toneClasses = TONE_CLASSES[tone];
  return (
    <div
      className={cn(
        'overflow-hidden rounded-3xl border bg-white px-6 py-8 text-center shadow-[0_2px_12px_rgba(0,0,0,0.06)]',
        toneClasses.border,
        className,
      )}
    >
      <div className={cn('mx-auto flex h-16 w-16 items-center justify-center rounded-full', toneClasses.circle)}>
        {icon ?? <DefaultIcon tone={tone} />}
      </div>
      <h2 className="mt-5 text-[19px] font-bold text-slate-800">{title}</h2>
      {description ? <p className="mt-2 text-sm leading-relaxed text-slate-500">{description}</p> : null}
      <div className="mt-7 space-y-3">
        <button
          type="button"
          onClick={onRetry}
          className="w-full rounded-xl bg-brand px-6 py-3 text-base font-semibold text-white shadow-[0_2px_12px_rgba(63,146,138,0.25)] transition active:scale-[0.99]"
        >
          {retryLabel}
        </button>
        {secondaryLabel && onSecondary ? (
          <button
            type="button"
            onClick={onSecondary}
            className="w-full rounded-xl px-6 py-2.5 text-sm font-medium text-slate-500 transition hover:text-slate-700"
          >
            {secondaryLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
